import { HTTP_CONTENT_TYPE_HEADER, HTTP_CONTENT_TYPE_JSON, HTTP_METHOD } from './consts';
import { isAbortError, networkError, schemaValidationError, unknownError } from './errors';
import type { ApiError, TransportConfig, RequestOptions, Transport } from './types';
import { buildFinalUrl } from './utils';

const DEFAULT_API_ERROR_HANDLER = (status: number, error: unknown) =>
  unknownError(`API error: ${status}`, status, error);

// Wraps a failed fetch or body read — an abort is reported distinctly from other network failures
const mapToNetworkError = (error: unknown) =>
  isAbortError(error)
    ? networkError('Request was aborted', undefined, error)
    : networkError('Network request failed', undefined, error);

// Centralizes API response error handling by parsing the error body and invoking the error handler
const handleApiResponseError = async (
  response: Response,
  apiErrorHandler: (status: number, error: unknown) => ApiError
): Promise<never> => {
  let errorText: string;

  // The body read fails when the request is aborted or the connection drops mid-response
  try {
    errorText = await response.text();
  } catch (readError) {
    return Promise.reject(mapToNetworkError(readError));
  }

  let error: unknown;

  try {
    error = JSON.parse(errorText);
  } catch {
    // If JSON parsing fails, use the raw text as the error
    error = errorText;
  }

  return Promise.reject(apiErrorHandler(response.status, error));
};

/**
 * Creates a configured HTTP transport function with JSON serialization
 * and runtime type validation via Zod schemas.
 * Authentication is handled via the defaultHeaders configuration.
 *
 * @returns A function that makes type-safe HTTP requests to the configured API
 */
export const createTransport = ({
  baseUrl,
  getDefaultHeaders = () => ({}),
  apiErrorHandler = DEFAULT_API_ERROR_HANDLER
}: TransportConfig) => {
  const transport: Transport = async <T>(
    endpoint: string,
    options: RequestOptions<T>
  ): Promise<T | { data: T; status: number }> => {
    const method = options.method ?? HTTP_METHOD.GET;

    // Build URL with query parameters
    const finalUrl = buildFinalUrl(endpoint, baseUrl, options?.queryParams);

    const defaultHeaders = getDefaultHeaders();

    // Automatically serialize object bodies to JSON and set appropriate Content-Type
    const isBodyObject = options.body && typeof options.body === 'object';

    const headers = {
      ...defaultHeaders,
      ...options.headers,
      ...(isBodyObject ? { [HTTP_CONTENT_TYPE_HEADER]: HTTP_CONTENT_TYPE_JSON } : {})
    };

    // If the body is an object, stringify it
    const body = isBodyObject ? JSON.stringify(options.body) : (options.body as BodyInit | undefined);

    try {
      const response = await fetch(finalUrl, {
        method,
        headers,
        body,
        signal: options.signal
      });

      if (!response.ok) {
        return handleApiResponseError(response, apiErrorHandler);
      }

      let text: string;

      try {
        text = await response.text();
      } catch (readError) {
        return Promise.reject(mapToNetworkError(readError));
      }

      let data: unknown;

      // Success responses may legitimately have no body (204 No Content, bodyless 201/202) —
      // parse JSON only when a body is present; the deserializer decides whether `undefined`
      // is acceptable.
      try {
        data = text ? JSON.parse(text) : undefined;
      } catch (parseError) {
        return Promise.reject(unknownError('Failed to parse response JSON', undefined, parseError));
      }

      // Validate response shape at runtime to ensure type safety
      const result = options.deserializer.safeParse(data);

      if (!result.success) {
        return Promise.reject(
          schemaValidationError(`Response validation failed: ${result.error.message}`, undefined, result.error)
        );
      }

      if (options.includeStatus) {
        return { data: result.data, status: response.status };
      }

      return result.data;
    } catch (error: unknown) {
      return Promise.reject(mapToNetworkError(error));
    }
  };

  return transport;
};
